import React from "react";
import {
  Typography,
  Box,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Refresh,
  Visibility,
  Delete,
  AttachMoney,
} from "@mui/icons-material";
import { TableComponent } from "../../../components/Table";
import { GridColDef } from "@mui/x-data-grid";

interface ProductTableProps {
  productosFiltrados: any[];
  cargando: boolean;
  palette: any;
  category?: string;
  modoVisualizacion: string;
  cargarProductos: () => void;
  handleViewProduct: (producto: any) => void;
  handleOpenSena: (producto: any) => void;
  handleDeleteProduct: (id: number) => void;
}

export const ProductTable: React.FC<ProductTableProps> = ({
  productosFiltrados,
  cargando,
  palette,
  category,
  modoVisualizacion,
  cargarProductos,
  handleViewProduct,
  handleOpenSena,
  handleDeleteProduct,
}) => {
  // Estilo común para los encabezados siguiendo el diseño del sistema
  const headerStyle = {
    color: "#8c8c8c", // Color grisáceo elegante común en el sistema Vittorio
    fontWeight: 700,
    fontSize: "0.85rem",
    textTransform: "uppercase",
  };

  // Definimos las columnas dinámicamente según el modo de visualización
  const columnas: GridColDef[] = modoVisualizacion === "finalizados" 
    ? [
        { 
          field: "concepto", 
          headerName: "Producto", 
          flex: 2,
          minWidth: 400,
          align: "center",
          headerAlign: "center",
          renderHeader: () => <Typography sx={headerStyle}>Producto</Typography>,
          renderCell: (params) => (
            <Typography sx={{ fontWeight: 500, color: '#444' }}>
              {params.value || "-"}
            </Typography>
          ),
        },
        {
          field: "anioViaje",
          headerName: "Año",
          width: 120,
          align: "center",
          headerAlign: "center",
          renderHeader: () => <Typography sx={headerStyle}>Año</Typography>,
          renderCell: (params) => (
            <Typography sx={{ fontWeight: "bold", color: '#444' }}>
              {params.value || "-"}
            </Typography>
          ),
        },
        {
          field: "categoriaFormateada",
          headerName: "Tipo",
          width: 200,
          align: "center",
          headerAlign: "center",
          renderHeader: () => <Typography sx={headerStyle}>Tipo</Typography>,
          renderCell: (params) => (
            <Typography sx={{ color: '#666' }}>
              {params.value || "Sin tipo"}
            </Typography>
          ),
        },
        {
          field: "actions",
          headerName: "Acciones",
          width: 120,
          align: "center",
          headerAlign: "center",
          sortable: false,
          renderHeader: () => <Typography sx={headerStyle}>Acciones</Typography>,
          renderCell: (params) => (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <IconButton
                size="small"
                sx={{ color: palette.main }}
                onClick={() => handleViewProduct(params.row)}
                title="Ver ficha"
              >
                <Visibility fontSize="small" />
              </IconButton>
            </Box>
          ),
        },
      ]
    : [
        {
          field: "actions",
          headerName: "Acciones",
          width: 120,
          align: "center",
          headerAlign: "center",
          sortable: false,
          renderHeader: () => <Typography sx={headerStyle}>Acciones</Typography>,
          renderCell: (params) => (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <IconButton
                size="small"
                sx={{ color: palette.main }}
                onClick={() => handleViewProduct(params.row)}
                title="Ver ficha"
              >
                <Visibility fontSize="small" />
              </IconButton>
              {category === "egresados" && params.row.moneda === "ARS" && (
                <IconButton
                  size="small"
                  sx={{ color: "#5ba4e5" }}
                  onClick={() => handleOpenSena(params.row)}
                  title="Señas"
                >
                  <AttachMoney fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                color="error"
                onClick={() => handleDeleteProduct(params.row.id)}
                title="Eliminar"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          ),
        },
        {
          field: "anioViaje",
          headerName: "Año",
          width: 100,
          align: "center",
          headerAlign: "center",
          renderHeader: () => <Typography sx={headerStyle}>Año</Typography>,
          renderCell: (params) => (
            <Typography sx={{ fontWeight: "bold" }}>
              {params.value || "-"}
            </Typography>
          ),
        },
        { 
          field: "concepto", 
          headerName: "Concepto", 
          flex: 1, 
          minWidth: 300,
          renderHeader: () => <Typography sx={headerStyle}>Concepto</Typography>,
        },
      ];

  if (cargando)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", color: palette.main }}
        >
          {modoVisualizacion === "vigentes"
            ? "Productos Vigentes"
            : "Productos Finalizados"}{" "}
          {category ? `(${category.toUpperCase()})` : ""}
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={cargarProductos}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 2 }}
        >
          Actualizar
        </Button>
      </Box>

      <TableComponent
        columnData={columnas}
        rowData={productosFiltrados}
        exportTitle={`Paquetes_Grupales_${category || "all"}`}
        hideSearch={true}
      />
    </Box>
  );
};
